import React from 'react';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useHoldRequests } from '@/hooks/useHoldRequests';
import { DashboardMetrics } from '../DashboardMetrics';
import { QuickActions } from '../QuickActions';
import { RecentActivity } from '../RecentActivity';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui';

export function ServiceAEDashboard() {
  const { user } = useAuthContext();
  const { data: holdRequestsData, isLoading } = useHoldRequests();

  // Filter for cross-region requests where user is assigned as Service AE to any line items
  // Note: Service AE is now at line item level, so we show all cross-region requests
  // The backend will filter line items to show only those assigned to this Service AE
  const crossRegionRequests = React.useMemo(() => {
    if (!holdRequestsData?.holdRequests || !user) return [];
    // Show all cross-region requests - the user will see line items assigned to them
    return holdRequestsData.holdRequests.filter((r) => r.isCrossRegion);
  }, [holdRequestsData, user]);

  const metrics = React.useMemo(() => {
    // Use server-provided statusCounts for cross-region requests
    // Since service_ae filtering returns only isCrossRegion=true from the backend,
    // the statusCounts already reflect the correct scoped data
    const counts = holdRequestsData?.statusCounts || {};
    const activeRequests = (counts.open || 0) + (counts.partial_response_received || 0);
    const pendingActions = counts.partial_response_received || 0;

    const today = new Date().toDateString();
    const completedToday = crossRegionRequests.filter(
      (r) =>
        r.workflowStatus !== 'cancelled' &&
        r.derivedStatus === 'request_completed_active' &&
        new Date(r.updatedAt).toDateString() === today
    ).length;

    return {
      activeRequests,
      pendingActions,
      completedToday,
    };
  }, [holdRequestsData, crossRegionRequests]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-neutral-900">Service AE Dashboard</h1>
        <p className="mt-2 text-neutral-600">
          Welcome back, {user?.firstName}! Manage cross-region requests assigned to you.
        </p>
      </div>

      <Card className="bg-outfront-purple-light border-outfront-purple">
        <CardHeader>
          <CardTitle className="text-neutral-900">Cross-Region Support</CardTitle>
          <CardDescription className="text-outfront-purple">
            You have access to requests where you are assigned as Service AE
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-outfront-purple">
            As a Service AE, you can view and coordinate on cross-region hold requests.
            You are assigned to specific line items and can edit flight dates on those line items.
          </p>
        </CardContent>
      </Card>

      <DashboardMetrics metrics={metrics} />

      <QuickActions role="service_ae" />

      <RecentActivity requests={crossRegionRequests} isLoading={isLoading} />
    </div>
  );
}
